import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import {
    Table as TableSimple,
    type TableColumn,
    type TableAction,
} from "@/components/Table";
import { Modal } from "@/components/Modal";
import { passwordUserGroupApi } from "@/lib/api";
import { formatDate } from "@/utils/format";
import { PasswordUserGroup, PasswordUserGroupFormData } from "@/types/password";
import { useToast } from "@/context/ToastContext";
import { Badge } from "@/components/Badge";
import { SearchableMultiSelect } from "@/components/SearchableMultiSelect";

interface User {
    id: number;
    name: string;
    email: string;
}

interface PasswordUserGroupsViewProps {
    searchQuery: string;
    onSearchChange: (value: string) => void;
    users: User[];
    triggerCreate?: number;
}

export function PasswordUserGroupsView({
    searchQuery,
    onSearchChange,
    users,
    triggerCreate,
}: PasswordUserGroupsViewProps) {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();

    const [userGroups, setUserGroups] = useState<PasswordUserGroup[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [selectedGroup, setSelectedGroup] =
        useState<PasswordUserGroup | null>(null);
    const [isDetailsModalOpen, setIsDetailsModalOpen] =
        useState<boolean>(false);
    const [formData, setFormData] = useState<PasswordUserGroupFormData>({
        name: "",
        description: "",
        member_ids: [],
    });
    const [saving, setSaving] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);

    const openDetails = (group: PasswordUserGroup) => {
        setSelectedGroup(group);
        setIsDetailsModalOpen(true);
    };

    const loadGroups = async (): Promise<void> => {
        setLoading(true);
        try {
            const data = await passwordUserGroupApi.getAll();
            setUserGroups(data.data || data);
        } catch {
            showError(t("common.error"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadGroups();
    }, []);

    const prevTriggerCreate = useRef<number>(triggerCreate || 0);

    useEffect(() => {
        if (triggerCreate && triggerCreate > prevTriggerCreate.current) {
            setFormData({ name: "", description: "", member_ids: [] });
            setIsCreateModalOpen(true);
        }
        prevTriggerCreate.current = triggerCreate || 0;
    }, [triggerCreate]);

    const filteredGroups = searchQuery.trim()
        ? userGroups.filter((g) =>
              g.name.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : userGroups;

    const handleCreate = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();
        setSaving(true);
        try {
            await passwordUserGroupApi.create(formData);
            showSuccess(t("passwords.user_groups.created"));
            setIsCreateModalOpen(false);
            setFormData({ name: "", description: "", member_ids: [] });
            loadGroups();
        } catch {
            showError(t("common.error"));
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();
        if (!selectedGroup) return;
        setSaving(true);
        try {
            await passwordUserGroupApi.update(selectedGroup.id, formData);
            showSuccess(t("passwords.user_groups.updated"));
            setIsEditModalOpen(false);
            setSelectedGroup(null);
            loadGroups();
        } catch {
            showError(t("common.error"));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (): Promise<void> => {
        if (!selectedGroup) return;
        setDeleting(true);
        try {
            await passwordUserGroupApi.delete(selectedGroup.id);
            showSuccess(t("passwords.user_groups.deleted"));
            setIsDeleteModalOpen(false);
            setSelectedGroup(null);
            loadGroups();
        } catch {
            showError(t("common.error"));
        } finally {
            setDeleting(false);
        }
    };

    const openEdit = (group: PasswordUserGroup): void => {
        setSelectedGroup(group);
        setFormData({
            name: group.name,
            description: group.description || "",
            member_ids: group.members?.map((m) => m.id) || [],
        });
        setIsEditModalOpen(true);
    };

    const openDelete = (group: PasswordUserGroup): void => {
        setSelectedGroup(group);
        setIsDeleteModalOpen(true);
    };

    const userOptions = (users || []).map((u) => ({
        id: u.id,
        name: u.name,
        subtitle: u.email,
    }));

    const columns: TableColumn<PasswordUserGroup>[] = [
        {
            key: "name",
            label: t("common.name"),
            sortable: true,
        },
        {
            key: "description",
            label: t("common.description"),
        },
        {
            key: "members",
            label: t("passwords.user_groups.members"),
            type: "multivalue",
            max: 0,
            multiValueExtractor: (row) => row.members?.map((m) => m.name) || [],
        },
        {
            key: "updated_at",
            label: t("passwords.groups.updated_at"),
            sortable: true,
            render: (row) => (
                <div className="">
                    {row.updated_at ? formatDate(row.updated_at, false) : ""}
                </div>
            ),
        },
    ];

    const actions: TableAction<PasswordUserGroup>[] = [
        {
            label: t("common.edit"),
            icon: <Icon value="edit" size={16} />,
            onClick: openEdit,
        },
        {
            label: t("common.delete"),
            icon: <Icon value="delete" size={16} />,
            variant: "delete",
            onClick: openDelete,
        },
    ];

    const renderForm = (): React.ReactNode => (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1 w-full">
                <label className="text-sm font-medium text-slate-700 dark:text-zinc-300">
                    {t("common.name")}
                </label>
                <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                        setFormData((prev) => ({
                            ...prev,
                            name: e.target.value,
                        }))
                    }
                    className="w-full border border-slate-200 dark:border-zinc-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-zinc-800 text-slate-800 dark:text-zinc-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
                    required
                />
            </div>
            <div className="flex flex-col gap-1 w-full">
                <label className="text-sm font-medium text-slate-700 dark:text-zinc-300">
                    {t("common.description")}
                </label>
                <input
                    type="text"
                    value={formData.description || ""}
                    onChange={(e) =>
                        setFormData((prev) => ({
                            ...prev,
                            description: e.target.value,
                        }))
                    }
                    className="w-full border border-slate-200 dark:border-zinc-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-zinc-800 text-slate-800 dark:text-zinc-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
            </div>
            <div className="w-full">
                <SearchableMultiSelect
                    label={t("passwords.user_groups.members")}
                    options={userOptions}
                    value={formData.member_ids || []}
                    onChange={(ids) =>
                        setFormData((prev) => ({
                            ...prev,
                            member_ids: ids as number[],
                        }))
                    }
                    placeholder={t("passwords.form.shared_placeholder")}
                />
            </div>
        </div>
    );

    return (
        <div className="flex flex-col gap-4 w-full animate-fade-in">
            <TableSimple
                viewKey="vigepra_password_user_groups"
                searchValue={searchQuery}
                onSearchChange={onSearchChange}
                appearance="workspace"
                data={filteredGroups}
                columns={columns}
                actions={actions}
                actionMenu={true}
                searchable={true}
                loading={loading}
                emptyText={t("passwords.user_groups.empty")}
                onRowClick={openDetails}
            />

            {/* Create Modal */}
            <Modal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                title={t("passwords.user_groups.create")}
                onSubmit={handleCreate}
                submitLabel={t("common.save")}
                isSubmitting={saving}
            >
                {renderForm()}
            </Modal>

            {/* Edit Modal */}
            <Modal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                title={t("passwords.user_groups.edit")}
                onSubmit={handleEdit}
                submitLabel={t("common.save")}
                isSubmitting={saving}
            >
                {renderForm()}
            </Modal>

            {/* Delete Modal */}
            <Modal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                title={t("common.delete")}
                onSubmit={handleDelete}
                submitLabel={t("common.delete")}
                submitVariant="danger"
                isSubmitting={deleting}
            >
                <div className="text-slate-700 dark:text-zinc-300">
                    {t("passwords.user_groups.delete_confirm")}
                </div>
                <div className="text-center font-bold mt-2">
                    {selectedGroup?.name}
                </div>
            </Modal>

            {/* Details Modal */}
            <Modal
                isOpen={isDetailsModalOpen}
                onClose={() => setIsDetailsModalOpen(false)}
                title={t("passwords.user_groups.title")}
                footer={
                    <div className="flex items-center justify-center gap-2 w-full">
                        <button
                            className="btn warning"
                            onClick={() => {
                                setIsDetailsModalOpen(false);
                                openEdit(selectedGroup!);
                            }}
                        >
                            <Icon value="edit" size={16} />
                            {t("common.edit")}
                        </button>
                        <button
                            className="btn danger"
                            onClick={() => {
                                setIsDetailsModalOpen(false);
                                openDelete(selectedGroup!);
                            }}
                        >
                            <Icon value="delete" size={16} />
                            {t("common.delete")}
                        </button>
                    </div>
                }
            >
                {selectedGroup && (
                    <div className="flex flex-col gap-4 pt-2 text-center">
                        <div className="flex flex-col items-center">
                            <span className="text-sm font-bold text-slate-700 dark:text-zinc-300">
                                {t("common.name")}
                            </span>
                            <p className="text-sm text-slate-600 dark:text-zinc-400">
                                {selectedGroup.name}
                            </p>
                        </div>
                        {selectedGroup.description && (
                            <div className="flex flex-col items-center">
                                <span className="text-sm font-bold text-slate-700 dark:text-zinc-300">
                                    {t("common.description")}
                                </span>
                                <p className="text-sm text-slate-600 dark:text-zinc-400">
                                    {selectedGroup.description}
                                </p>
                            </div>
                        )}
                        <div className="flex flex-col items-center">
                            <span className="text-sm font-bold text-slate-700 dark:text-zinc-300">
                                {t("passwords.user_groups.members")}
                            </span>
                            <div className="flex flex-wrap justify-center gap-2 mt-2">
                                {selectedGroup.members?.length ? (
                                    selectedGroup.members.map((m) => (
                                        <Badge key={m.id} variant="primary">
                                            {m.name}
                                        </Badge>
                                    ))
                                ) : (
                                    <span className="text-sm text-slate-400">
                                        -
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
